import { useNextSanityImage } from 'next-sanity-image'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import type { BlogPost } from '@/cms/blog'
import { sanityClientLike } from '@/cms/client'
import type { Page } from '@/cms/pages'
import { DOMAIN } from '@/config/env'

export const PageMetaDescription = (props: {
  metaDescription?: Page['metaDescription'] | BlogPost['metaDescription']
}) => {
  const { asPath, locale } = useRouter()
  const canonicalUrl = useMemo(() => {
    const url = new URL(`/${locale}${asPath}`, `https://${DOMAIN}`)
    url.search = ''
    url.hash = ''
    return url.toString()
  }, [asPath, locale])

  const additionalHeadTags = useMemo(
    () => ({
      link: [
        {
          rel: 'canonical',
          href: canonicalUrl,
        },
      ],
    }),
    [canonicalUrl]
  )

  const { metaDescription } = props
  const imageProps = useNextSanityImage(
    sanityClientLike,
    metaDescription && 'metaImage' in metaDescription ? metaDescription?.metaImage ?? null : null,
    {
      imageBuilder: (imageUrlBuilder) => imageUrlBuilder.width(960).height(560),
    }
  )

  const schemaGraph = useMemo(
    () => `
{
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.hellogetsafe.com/#organization",
      "name": "Getsafe",
      "url": "https://www.hellogetsafe.com/",
      "sameAs": [
        "https://twitter.com/hellogetsafe",
        "https://www.instagram.com/hellogetsafe/",
        "https://www.linkedin.com/company/getsafe-personal-insurance/",
        "https://twitter.com/GetsafeDE"
      ],
      "logo": {
        "@type": "ImageObject",
        "@id": "https://www.hellogetsafe.com/#logo",
        "url": "${
          imageProps?.src ?? 'https://www.hellogetsafe.com/assets/images/getsafe_logo.png'
        }",
        "width": 800,
        "height": 210,
        "caption": "getsafe"
      },
      "image": {
        "@id": "https://www.hellogetsafe.com/#logo"
      }
    },
    {
      "@type" : "WebSite",
      "name" : "Getsafe",
      "url" : "https://www.hellogetsafe.com/"
    }
  ]
}
`,
    [imageProps?.src]
  )

  return (
    <Head>
      <title>{metaDescription?.title ?? 'Getsafe'}</title>
      <meta name="description" content={metaDescription?.description ?? ''} />

      <meta name="og:site" content="Getsafe" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={metaDescription?.title ?? 'Getsafe'} />
      <meta
        property="og:description"
        content={metaDescription?.description ?? ''}
        key="og:description"
      />
      <meta property="og:url" content={canonicalUrl.toString()} key="og:url" />
      <meta property="og:image" content={imageProps?.src} key="og:image" />

      <meta name="twitter:site" content="@hellogetsafe" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:image" content={imageProps?.src} key="twitter:image" />
      {additionalHeadTags.link.map((link, i) => (
        <link {...link} key={`additional_link_tags_${i.toString()}`} />
      ))}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaGraph }} />
    </Head>
  )
}
